// Pure chart.js configuration shared by the on-screen *Viz components and
// the image exporter, so both render from the same data + options with an
// explicit palette instead of reading theme tokens inline.

import { withAlpha } from '../theme';
import { pieColor } from './mapIcons';
import { seriesColor } from './palette';

// Axis label for a field key.
export function fieldLabel(fields, key) {
  if (!key) return null;
  const field = (fields || []).find((f) => f.key === key);
  return field ? field.label : key;
}

// Multi-series payload: { labels, series: [{ label, data }] } (from
// prepareSeries / prepareLineSeries). Single-series data stays an
// array of { label, value } entries. The stacked-bar payload also has a
// `series` array, but its entries carry `series` (not `data`), so this
// only matches the multi shape.
export function isMulti(data) {
  if (!data || Array.isArray(data) || !Array.isArray(data.series)) return false;
  return (data.series[0] || {}).data !== undefined;
}

export function buildBar(data, stacked, palette) {
  if (isMulti(data)) {
    const chartData = {
      labels: data.labels,
      datasets: (data.series || []).map((s, i) => ({
        label: s.label,
        data: s.data,
        backgroundColor: withAlpha(pieColor(i), 0.85)
      }))
    };
    return { data: chartData, options: barOptions(stacked, data.series.length > 1, palette) };
  }
  const entries = !stacked && Array.isArray(data) ? data.filter((d) => d.value !== null) : [];
  const chartData = stacked
    ? {
        labels: (data || {}).labels || [],
        datasets: ((data || {}).series || []).map((s, i) => ({
          label: s.label,
          data: s.series,
          backgroundColor: withAlpha(pieColor(i), 0.85)
        }))
      }
    : {
        labels: entries.map((d) => d.label),
        datasets: [
          {
            label: 'value',
            data: entries.map((d) => d.value),
            backgroundColor: withAlpha(palette.primary, 0.65)
          }
        ]
      };
  const options = barOptions(stacked, Boolean(stacked), palette);
  return { data: chartData, options };
}

function barOptions(stacked, showLegend, palette) {
  return {
    maintainAspectRatio: false,
    scales: {
      x: { stacked: Boolean(stacked), ticks: { color: palette.onSurface } },
      y: { beginAtZero: true, stacked: Boolean(stacked), ticks: { color: palette.onSurface } }
    },
    plugins: {
      legend: { display: showLegend, position: 'bottom', labels: { color: palette.onSurface } }
    }
  };
}

export function buildLine(data, label, palette) {
  if (isMulti(data)) {
    const series = data.series || [];
    const chartData = {
      labels: data.labels,
      datasets: series.map((s, i) => ({
        label: s.label,
        data: s.data,
        fill: false,
        tension: 0.2,
        borderColor: seriesColor(i, palette)
      }))
    };
    return {
      data: chartData,
      options: {
        maintainAspectRatio: false,
        scales: {
          x: { ticks: { color: palette.onSurface } },
          y: { ticks: { color: palette.onSurface } }
        },
        plugins: {
          legend: {
            display: series.length > 1,
            position: 'bottom',
            labels: { color: palette.onSurface }
          }
        }
      }
    };
  }
  const chartData = {
    labels: (data || []).map((d) => d.label),
    datasets: [
      {
        label: label || 'value',
        data: (data || []).map((d) => d.value),
        fill: false,
        tension: 0.2,
        borderColor: palette.secondary
      }
    ]
  };
  const options = {
    maintainAspectRatio: false,
    scales: {
      x: { ticks: { color: palette.onSurface } },
      y: { ticks: { color: palette.onSurface } }
    },
    plugins: { legend: { display: false } }
  };
  return { data: chartData, options };
}

export function buildPie(data, palette) {
  if (isMulti(data)) {
    // One dataset per series: Chart.js renders them as concentric pies.
    const chartData = {
      datasets: (data.series || []).map((s, i) => ({
        label: s.label,
        data: s.data,
        backgroundColor: s.data.map(
          (_, j) => `hsl(${(j * 360) / Math.max(s.data.length, 1)}, 60%, ${i === 0 ? 50 : 65}%)`
        )
      }))
    };
    return {
      data: chartData,
      options: {
        maintainAspectRatio: false,
        plugins: { legend: { position: 'right', labels: { color: palette.onSurface } } }
      }
    };
  }
  const entries = (data || []).filter((d) => d.value !== null);
  const chartData = {
    labels: entries.map((d) => d.label),
    datasets: [
      {
        data: entries.map((d) => d.value),
        backgroundColor: entries.map(
          (_, i) => `hsl(${(i * 360) / Math.max(entries.length, 1)}, 60%, 50%)`
        )
      }
    ]
  };
  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'right', labels: { color: palette.onSurface } },
      tooltip: {
        callbacks: {
          afterLabel: (ctx) => {
            const entry = entries[ctx.dataIndex];
            return entry && entry.count !== undefined ? `records: ${entry.count}` : null;
          }
        }
      }
    }
  };
  return { data: chartData, options };
}

export function buildRadar(data, palette) {
  if (isMulti(data)) {
    const series = data.series || [];
    const chartData = {
      labels: data.labels,
      datasets: series.map((s, i) => ({
        label: s.label,
        data: s.data,
        fill: true,
        backgroundColor: withAlpha(seriesColor(i, palette), 0.25),
        borderColor: seriesColor(i, palette)
      }))
    };
    return {
      data: chartData,
      options: {
        maintainAspectRatio: false,
        scales: { r: { ticks: { color: palette.onSurface } } },
        plugins: {
          legend: {
            display: series.length > 1,
            position: 'bottom',
            labels: { color: palette.onSurface }
          }
        }
      }
    };
  }
  const entries = (data || []).filter((d) => d.value !== null);
  const chartData = {
    labels: entries.map((d) => d.label),
    datasets: [
      {
        label: 'value',
        data: entries.map((d) => d.value),
        fill: true,
        backgroundColor: withAlpha(palette.primary, 0.3),
        borderColor: palette.primary
      }
    ]
  };
  const options = {
    maintainAspectRatio: false,
    scales: { r: { ticks: { color: palette.onSurface } } },
    plugins: { legend: { display: false } }
  };
  return { data: chartData, options };
}

export function buildScatter(points, xLabel, yLabel, palette) {
  const series = new Map();
  for (const p of points || []) {
    const name = p.series || 'All';
    if (!series.has(name)) series.set(name, []);
    series.get(name).push({ x: p.x, y: p.y });
  }
  const datasets = Array.from(series.entries()).map(([label, data], i) => ({
    label,
    data,
    showLine: false,
    pointRadius: 4,
    backgroundColor: withAlpha(seriesColor(i, palette), 0.7),
    borderColor: seriesColor(i, palette)
  }));
  const options = {
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'linear',
        ticks: { color: palette.onSurface },
        title: { display: Boolean(xLabel), text: xLabel || 'x' }
      },
      y: {
        ticks: { color: palette.onSurface },
        title: { display: Boolean(yLabel), text: yLabel || 'y' }
      }
    },
    plugins: { legend: { display: series.size > 1, labels: { color: palette.onSurface } } }
  };
  return { data: { datasets }, options };
}
